import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, normalizeClass as _normalizeClass, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeStyle as _normalizeStyle, Fragment as _Fragment, createBlock as _createBlock, withModifiers as _withModifiers, withKeys as _withKeys, Transition as _Transition } from "vue"

const _hoisted_1 = { class: "settings-panel" }
const _hoisted_2 = { class: "settings-panel__sidebar" }
const _hoisted_3 = { class: "settings-panel__nav" }
const _hoisted_4 = { class: "settings-panel__main" }
const _hoisted_5 = { class: "settings-panel__header" }
const _hoisted_6 = { class: "settings-panel__title" }
const _hoisted_7 = { class: "settings-panel__content" }
const _hoisted_8 = {
  key: 0,
  class: "settings-panel__info-alert"
}
const _hoisted_9 = { class: "settings-panel__info-alert-icon" }
const _hoisted_10 = {
  key: 1,
  class: "settings-panel__info-alert"
}
const _hoisted_11 = { class: "settings-panel__info-alert-icon" }
const _hoisted_12 = { class: "settings-panel__input-group" }
const _hoisted_13 = { class: "settings-panel__test-section" }
const _hoisted_14 = { class: "settings-panel__test-status" }
const _hoisted_15 = {
  key: 0,
  class: "settings-panel__test-placeholder"
}
const _hoisted_16 = {
  key: 1,
  class: "settings-panel__level-meter"
}
const _hoisted_17 = { class: "settings-panel__input-group" }
const _hoisted_18 = {
  key: 0,
  class: "settings-panel__info-alert"
}
const _hoisted_19 = { class: "settings-panel__info-alert-icon" }
const _hoisted_20 = {
  key: 1,
  class: "settings-panel__info-alert"
}
const _hoisted_21 = { class: "settings-panel__info-alert-icon" }
const _hoisted_22 = { class: "settings-panel__video-preview" }
const _hoisted_23 = {
  key: 2,
  class: "settings-panel__video-status"
}
const _hoisted_24 = { class: "settings-panel__toggle-group" }
const _hoisted_25 = { class: "settings-panel__switch-label" }
const _hoisted_26 = { class: "settings-panel__input-group" }

import { ref, computed, watch, onUnmounted, nextTick } from 'vue'

interface MediaDeviceInfo {
  deviceId: string
  label: string
  kind: string
}

interface Props {
  modelValue: boolean
}

interface Emits {
  (e: 'update:modelValue', value: boolean): void
  (e: 'close'): void
}


export default /*@__PURE__*/_defineComponent({
  __name: 'SettingsPanel',
  props: {
    modelValue: { type: Boolean }
  },
  emits: ["update:modelValue", "close"],
  setup(__props: any, { emit: __emit }) {

const props = __props
const emit = __emit

const activeTab = ref<'audio' | 'video'>('audio')

// -- Device lists --
const microphoneDevices = ref<MediaDeviceInfo[]>([])
const speakerDevices = ref<MediaDeviceInfo[]>([])
const cameraDevices = ref<MediaDeviceInfo[]>([])

// -- Selected devices --
const microphoneDevice = ref<string>('')
const speakerDevice = ref<string>('')
const cameraDevice = ref<string>('')

// -- Permission state --
const micPermissionDenied = ref(false)
const cameraPermissionDenied = ref(false)

// -- Camera toggle --
const cameraEnabled = ref(true)
const videoStream = ref<MediaStream | null>(null)
const videoPreviewEl = ref<HTMLVideoElement | null>(null)

// -- Mic testing --
const isTesting = ref(false)
const micLevel = ref(0)
let audioContext: AudioContext | null = null
let analyserNode: AnalyserNode | null = null
let micStream: MediaStream | null = null
let animFrameId: number | null = null

// -- Speaker testing --
const isTestingSpeaker = ref(false)
let speakerAudio: HTMLAudioElement | null = null

// -- Computed helpers --
const microphoneDeviceItems = computed(() =>
  microphoneDevices.value.map((d, i) => ({
    deviceId: d.deviceId,
    label: d.label || `Microphone ${i + 1}`,
  }))
)

const speakerDeviceItems = computed(() =>
  speakerDevices.value.map((d, i) => ({
    deviceId: d.deviceId,
    label: d.label || `Speaker ${i + 1}`,
  }))
)

const cameraDeviceItems = computed(() =>
  cameraDevices.value.map((d, i) => ({
    deviceId: d.deviceId,
    label: d.label || `Camera ${i + 1}`,
  }))
)

const hasMicDevices = computed(() => microphoneDevices.value.length > 0)
const hasSpeakerDevices = computed(() => speakerDevices.value.length > 0)
const hasCameraDevices = computed(() => cameraDevices.value.length > 0)

// -- Device enumeration --
async function enumerateDevices() {
  try {
    // Request permission to get labelled device names
    await navigator.mediaDevices.getUserMedia({ audio: true }).then((s) => {
      s.getTracks().forEach((t) => t.stop())
    }).catch(() => {
      micPermissionDenied.value = true
    })

    const devices = await navigator.mediaDevices.enumerateDevices()

    microphoneDevices.value = devices
      .filter((d) => d.kind === 'audioinput')
      .map((d) => ({ deviceId: d.deviceId, label: d.label, kind: d.kind }))

    speakerDevices.value = devices
      .filter((d) => d.kind === 'audiooutput')
      .map((d) => ({ deviceId: d.deviceId, label: d.label, kind: d.kind }))

    cameraDevices.value = devices
      .filter((d) => d.kind === 'videoinput')
      .map((d) => ({ deviceId: d.deviceId, label: d.label, kind: d.kind }))

    if (hasMicDevices.value && !microphoneDevice.value) {
      microphoneDevice.value = microphoneDevices.value[0].deviceId
    }

    if (hasSpeakerDevices.value && !speakerDevice.value) {
      speakerDevice.value = speakerDevices.value[0].deviceId
    }

    if (hasCameraDevices.value && !cameraDevice.value) {
      cameraDevice.value = cameraDevices.value[0].deviceId
    }
  } catch {
    micPermissionDenied.value = true
  }
}

// -- Camera preview --
async function startCameraPreview(deviceId?: string) {
  stopCameraPreview()
  if (cameraPermissionDenied.value) return

  try {
    const constraints: MediaStreamConstraints = {
      video: deviceId ? { deviceId: { exact: deviceId } } : true,
    }
    const stream = await navigator.mediaDevices.getUserMedia(constraints)
    videoStream.value = stream
    await nextTick()
    if (videoPreviewEl.value) {
      videoPreviewEl.value.srcObject = stream
    }
  } catch (err: unknown) {
    if (err instanceof Error && err.name === 'NotAllowedError') {
      cameraPermissionDenied.value = true
    }
    videoStream.value = null
  }
}

function stopCameraPreview() {
  if (videoStream.value) {
    videoStream.value.getTracks().forEach((t) => t.stop())
    videoStream.value = null
  }
}

// -- Microphone test --
async function handleTestAudio() {
  if (isTesting.value) {
    stopMicTest()
    return
  }

  try {
    const constraints: MediaStreamConstraints = {
      audio: microphoneDevice.value ? { deviceId: { exact: microphoneDevice.value } } : true,
    }
    micStream = await navigator.mediaDevices.getUserMedia(constraints)
    audioContext = new AudioContext()
    const source = audioContext.createMediaStreamSource(micStream)
    analyserNode = audioContext.createAnalyser()
    analyserNode.fftSize = 256
    source.connect(analyserNode)

    isTesting.value = true
    animateMicLevel()
  } catch {
    micPermissionDenied.value = true
  }
}

function animateMicLevel() {
  if (!analyserNode || !isTesting.value) return

  const dataArray = new Uint8Array(analyserNode.frequencyBinCount)
  analyserNode.getByteTimeDomainData(dataArray)

  let sum = 0
  for (let i = 0; i < dataArray.length; i++) {
    const val = (dataArray[i] - 128) / 128
    sum += val * val
  }
  const rms = Math.sqrt(sum / dataArray.length)
  micLevel.value = Math.min(100, Math.round(rms * 400))

  animFrameId = requestAnimationFrame(animateMicLevel)
}

function stopMicTest() {
  isTesting.value = false
  micLevel.value = 0

  if (animFrameId !== null) {
    cancelAnimationFrame(animFrameId)
    animFrameId = null
  }
  if (micStream) {
    micStream.getTracks().forEach((t) => t.stop())
    micStream = null
  }
  if (audioContext) {
    audioContext.close()
    audioContext = null
  }
  analyserNode = null
}

// -- Speaker test --
function handleTestSpeaker() {
  if (isTestingSpeaker.value) return

  isTestingSpeaker.value = true
  speakerAudio = new Audio('/test-tone.mp3')

  // Fallback: generate a short beep via Web Audio API if no file
  const ctx = new AudioContext()
  const oscillator = ctx.createOscillator()
  const gainNode = ctx.createGain()
  oscillator.connect(gainNode)
  gainNode.connect(ctx.destination)
  oscillator.type = 'sine'
  oscillator.frequency.setValueAtTime(440, ctx.currentTime)
  gainNode.gain.setValueAtTime(0.3, ctx.currentTime)
  gainNode.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 1.5)
  oscillator.start(ctx.currentTime)
  oscillator.stop(ctx.currentTime + 1.5)

  oscillator.onended = () => {
    ctx.close()
    isTestingSpeaker.value = false
  }
}

// -- Event handlers --
const handleMicrophoneChange = (value: string) => {
  microphoneDevice.value = value
  if (isTesting.value) {
    stopMicTest()
  }
}

const handleSpeakerChange = (value: string) => {
  speakerDevice.value = value
}

const handleCameraChange = (value: string) => {
  cameraDevice.value = value
  if (cameraEnabled.value) {
    startCameraPreview(value)
  }
}

const handleCameraToggle = (enabled: boolean | null) => {
  if (enabled) {
    startCameraPreview(cameraDevice.value || undefined)
  } else {
    stopCameraPreview()
  }
}

const handleClose = () => {
  stopMicTest()
  stopCameraPreview()
  emit('update:modelValue', false)
  emit('close')
}

// -- Lifecycle watchers --
watch(
  () => props.modelValue,
  async (open) => {
    if (open) {
      await enumerateDevices()
      if (activeTab.value === 'video') {
        await startCameraPreview(cameraDevice.value || undefined)
      }
    } else {
      stopMicTest()
      stopCameraPreview()
    }
  }
)

watch(activeTab, async (tab) => {
  if (tab === 'video') {
    if (!hasCameraDevices.value) {
      try {
        await navigator.mediaDevices.getUserMedia({ video: true }).then((s) => s.getTracks().forEach((t) => t.stop()))
        const devices = await navigator.mediaDevices.enumerateDevices()
        cameraDevices.value = devices
          .filter((d) => d.kind === 'videoinput')
          .map((d) => ({ deviceId: d.deviceId, label: d.label, kind: d.kind }))
        if (hasCameraDevices.value && !cameraDevice.value) {
          cameraDevice.value = cameraDevices.value[0].deviceId
        }
      } catch {
        cameraPermissionDenied.value = true
      }
    }
    if (cameraEnabled.value) {
      await startCameraPreview(cameraDevice.value || undefined)
    }
  } else {
    stopCameraPreview()
    stopMicTest()
  }
})

onUnmounted(() => {
  stopMicTest()
  stopCameraPreview()
})

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_select = _resolveComponent("v-select")!
  const _component_v_switch = _resolveComponent("v-switch")!

  return (_openBlock(), _createBlock(_Transition, { name: "settings-panel-slide" }, {
    default: _withCtx(() => [
      (__props.modelValue)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            class: "settings-panel-overlay",
            role: "button",
            tabindex: "0",
            onClick: _withModifiers(handleClose, ["self"]),
            onKeydown: _withKeys(handleClose, ["escape"])
          }, [
            _createElementVNode("div", _hoisted_1, [
              _createElementVNode("div", _hoisted_2, [
                _cache[7] || (_cache[7] = _createElementVNode("div", { class: "settings-panel__sidebar-header" }, [
                  _createElementVNode("div", { class: "settings-panel__sidebar-title" }, "Settings"),
                  _createElementVNode("div", { class: "settings-panel__sidebar-divider" })
                ], -1)),
                _createElementVNode("div", _hoisted_3, [
                  _createVNode(_component_v_btn, {
                    variant: "flat",
                    size: "small",
                    rounded: "pill",
                    class: _normalizeClass(['settings-panel__nav-btn', { 'settings-panel__nav-btn--active': activeTab.value === 'audio' }]),
                    onClick: _cache[0] || (_cache[0] = ($event: any) => (activeTab.value = 'audio'))
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_icon, {
                        start: "",
                        size: "24"
                      }, {
                        default: _withCtx(() => [...(_cache[3] || (_cache[3] = [
                          _createTextVNode("mdi-microphone", -1)
                        ]))]),
                        _: 1
                      }),
                      _cache[4] || (_cache[4] = _createTextVNode(" Audio ", -1))
                    ]),
                    _: 1
                  }, 8, ["class"]),
                  _createVNode(_component_v_btn, {
                    variant: "outlined",
                    size: "small",
                    rounded: "pill",
                    class: _normalizeClass(['settings-panel__nav-btn', { 'settings-panel__nav-btn--active': activeTab.value === 'video' }]),
                    onClick: _cache[1] || (_cache[1] = ($event: any) => (activeTab.value = 'video'))
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_icon, {
                        start: "",
                        size: "24"
                      }, {
                        default: _withCtx(() => [...(_cache[5] || (_cache[5] = [
                          _createTextVNode("mdi-video", -1)
                        ]))]),
                        _: 1
                      }),
                      _cache[6] || (_cache[6] = _createTextVNode(" Video ", -1))
                    ]),
                    _: 1
                  }, 8, ["class"])
                ])
              ]),
              _createElementVNode("div", _hoisted_4, [
                _createElementVNode("div", _hoisted_5, [
                  _createElementVNode("h2", _hoisted_6, _toDisplayString(activeTab.value === 'audio' ? 'Audio Settings' : 'Video Settings'), 1),
                  _createVNode(_component_v_btn, {
                    icon: "",
                    variant: "text",
                    size: "small",
                    class: "settings-panel__close-btn",
                    onClick: handleClose
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_icon, {
                        size: "20",
                        color: "#222222"
                      }, {
                        default: _withCtx(() => [...(_cache[8] || (_cache[8] = [
                          _createTextVNode("mdi-close", -1)
                        ]))]),
                        _: 1
                      })
                    ]),
                    _: 1
                  })
                ]),
                _createElementVNode("div", _hoisted_7, [
                  (activeTab.value === 'audio')
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                        _cache[18] || (_cache[18] = _createElementVNode("div", { class: "settings-panel__description" }, [
                          _createTextVNode(" Check your audio before starting a call."),
                          _createElementVNode("br"),
                          _createTextVNode(" During calls, controls are managed in Zoom. ")
                        ], -1)),
                        (micPermissionDenied.value)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                              _createElementVNode("div", _hoisted_9, [
                                _createVNode(_component_v_icon, {
                                  size: "22",
                                  color: "#E07B00"
                                }, {
                                  default: _withCtx(() => [...(_cache[9] || (_cache[9] = [
                                    _createTextVNode("mdi-information", -1)
                                  ]))]),
                                  _: 1
                                })
                              ]),
                              _cache[10] || (_cache[10] = _createElementVNode("span", { class: "settings-panel__info-alert-text" }, "Permissions denied", -1))
                            ]))
                          : _createCommentVNode("", true),
                        (!hasMicDevices.value && !micPermissionDenied.value)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                              _createElementVNode("div", _hoisted_11, [
                                _createVNode(_component_v_icon, {
                                  size: "22",
                                  color: "#E07B00"
                                }, {
                                  default: _withCtx(() => [...(_cache[11] || (_cache[11] = [
                                    _createTextVNode("mdi-information", -1)
                                  ]))]),
                                  _: 1
                                })
                              ]),
                              _cache[12] || (_cache[12] = _createElementVNode("span", { class: "settings-panel__info-alert-text" }, "No Device Found", -1))
                            ]))
                          : _createCommentVNode("", true),
                        _createElementVNode("div", _hoisted_12, [
                          _cache[14] || (_cache[14] = _createElementVNode("div", { class: "settings-panel__input-label" }, "Microphone input", -1)),
                          _createVNode(_component_v_select, {
                            "model-value": microphoneDevice.value,
                            items: microphoneDeviceItems.value,
                            "item-title": "label",
                            "item-value": "deviceId",
                            variant: "outlined",
                            density: "comfortable",
                            "hide-details": "",
                            disabled: micPermissionDenied.value || !hasMicDevices.value,
                            placeholder: "No microphone detected",
                            class: "settings-panel__select",
                            "onUpdate:modelValue": handleMicrophoneChange
                          }, {
                            "prepend-inner": _withCtx(() => [
                              _createVNode(_component_v_icon, {
                                size: "24",
                                color: "#222222"
                              }, {
                                default: _withCtx(() => [...(_cache[13] || (_cache[13] = [
                                  _createTextVNode("mdi-microphone-outline", -1)
                                ]))]),
                                _: 1
                              })
                            ]),
                            _: 1
                          }, 8, ["model-value", "items", "disabled"]),
                          _createVNode(_component_v_btn, {
                            variant: "flat",
                            size: "small",
                            rounded: "pill",
                            class: "settings-panel__test-btn",
                            disabled: micPermissionDenied.value || !hasMicDevices.value,
                            onClick: handleTestAudio
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(isTesting.value ? 'Stop Test' : 'Test Audio'), 1)
                            ]),
                            _: 1
                          }, 8, ["disabled"])
                        ]),
                        _createElementVNode("div", _hoisted_13, [
                          _cache[15] || (_cache[15] = _createElementVNode("div", { class: "settings-panel__input-label" }, "Microphone test", -1)),
                          _createElementVNode("div", _hoisted_14, [
                            (!isTesting.value)
                              ? (_openBlock(), _createElementBlock("span", _hoisted_15, "No input"))
                              : (_openBlock(), _createElementBlock("div", _hoisted_16, [
                                  _createElementVNode("div", {
                                    class: "settings-panel__level-bar",
                                    style: _normalizeStyle({ width: `${micLevel.value}%` })
                                  }, null, 4)
                                ]))
                          ])
                        ]),
                        _createElementVNode("div", _hoisted_17, [
                          _cache[17] || (_cache[17] = _createElementVNode("div", { class: "settings-panel__input-label" }, "Speaker output", -1)),
                          _createVNode(_component_v_select, {
                            "model-value": speakerDevice.value,
                            items: speakerDeviceItems.value,
                            "item-title": "label",
                            "item-value": "deviceId",
                            variant: "outlined",
                            density: "comfortable",
                            "hide-details": "",
                            disabled: !hasSpeakerDevices.value,
                            placeholder: "No speaker detected",
                            class: "settings-panel__select",
                            "onUpdate:modelValue": handleSpeakerChange
                          }, {
                            "prepend-inner": _withCtx(() => [
                              _createVNode(_component_v_icon, {
                                size: "24",
                                color: "#222222"
                              }, {
                                default: _withCtx(() => [...(_cache[16] || (_cache[16] = [
                                  _createTextVNode("mdi-volume-high", -1)
                                ]))]),
                                _: 1
                              })
                            ]),
                            _: 1
                          }, 8, ["model-value", "items", "disabled"]),
                          _createVNode(_component_v_btn, {
                            variant: "flat",
                            size: "small",
                            rounded: "pill",
                            class: "settings-panel__test-btn",
                            disabled: !hasSpeakerDevices.value || isTestingSpeaker.value,
                            onClick: handleTestSpeaker
                          }, {
                            default: _withCtx(() => [
                              _createTextVNode(_toDisplayString(isTestingSpeaker.value ? 'Playing...' : 'Test Speaker'), 1)
                            ]),
                            _: 1
                          }, 8, ["disabled"])
                        ])
                      ], 64))
                    : (activeTab.value === 'video')
                      ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                          _cache[27] || (_cache[27] = _createElementVNode("div", { class: "settings-panel__description" }, [
                            _createTextVNode(" Check your video before starting a call."),
                            _createElementVNode("br"),
                            _createTextVNode(" During calls, controls are managed in Zoom. ")
                          ], -1)),
                          (cameraPermissionDenied.value)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_18, [
                                _createElementVNode("div", _hoisted_19, [
                                  _createVNode(_component_v_icon, {
                                    size: "22",
                                    color: "#E07B00"
                                  }, {
                                    default: _withCtx(() => [...(_cache[19] || (_cache[19] = [
                                      _createTextVNode("mdi-information", -1)
                                    ]))]),
                                    _: 1
                                  })
                                ]),
                                _cache[20] || (_cache[20] = _createElementVNode("span", { class: "settings-panel__info-alert-text" }, "Permissions denied", -1))
                              ]))
                            : _createCommentVNode("", true),
                          (!hasCameraDevices.value && !cameraPermissionDenied.value)
                            ? (_openBlock(), _createElementBlock("div", _hoisted_20, [
                                _createElementVNode("div", _hoisted_21, [
                                  _createVNode(_component_v_icon, {
                                    size: "22",
                                    color: "#E07B00"
                                  }, {
                                    default: _withCtx(() => [...(_cache[21] || (_cache[21] = [
                                      _createTextVNode("mdi-information", -1)
                                    ]))]),
                                    _: 1
                                  })
                                ]),
                                _cache[22] || (_cache[22] = _createElementVNode("span", { class: "settings-panel__info-alert-text" }, "No Device Found", -1))
                              ]))
                            : _createCommentVNode("", true),
                          _createElementVNode("div", _hoisted_22, [
                            _createElementVNode("div", {
                              class: _normalizeClass(["settings-panel__video-placeholder", { 'settings-panel__video-placeholder--active': cameraEnabled.value && videoStream.value }])
                            }, [
                              (cameraEnabled.value && videoStream.value)
                                ? (_openBlock(), _createElementBlock("video", {
                                    key: 0,
                                    ref_key: "videoPreviewEl",
                                    ref: videoPreviewEl,
                                    class: "settings-panel__video-element",
                                    autoplay: "",
                                    muted: "",
                                    playsinline: ""
                                  }, null, 512))
                                : (_openBlock(), _createBlock(_component_v_icon, {
                                    key: 1,
                                    size: "48",
                                    color: "rgba(255,255,255,0.3)"
                                  }, {
                                    default: _withCtx(() => [...(_cache[23] || (_cache[23] = [
                                      _createTextVNode("mdi-video-off", -1)
                                    ]))]),
                                    _: 1
                                  })),
                              (cameraEnabled.value && videoStream.value)
                                ? (_openBlock(), _createElementBlock("div", _hoisted_23, "Camera active"))
                                : _createCommentVNode("", true)
                            ], 2)
                          ]),
                          _createElementVNode("div", _hoisted_24, [
                            _cache[24] || (_cache[24] = _createElementVNode("div", { class: "settings-panel__toggle-header" }, [
                              _createElementVNode("div", { class: "settings-panel__input-label" }, "Camera"),
                              _createElementVNode("div", { class: "settings-panel__toggle-description" }, "Toggle camera on or off")
                            ], -1)),
                            _createVNode(_component_v_switch, {
                              modelValue: cameraEnabled.value,
                              "onUpdate:modelValue": [
                                _cache[2] || (_cache[2] = ($event: any) => ((cameraEnabled).value = $event)),
                                handleCameraToggle
                              ],
                              color: "#795DE9",
                              "hide-details": "",
                              density: "compact",
                              class: "settings-panel__switch"
                            }, {
                              label: _withCtx(() => [
                                _createElementVNode("span", _hoisted_25, _toDisplayString(cameraEnabled.value ? 'On' : 'Off'), 1)
                              ]),
                              _: 1
                            }, 8, ["modelValue"])
                          ]),
                          _createElementVNode("div", _hoisted_26, [
                            _cache[26] || (_cache[26] = _createElementVNode("div", { class: "settings-panel__input-label" }, "Camera Device", -1)),
                            _createVNode(_component_v_select, {
                              "model-value": cameraDevice.value,
                              items: cameraDeviceItems.value,
                              "item-title": "label",
                              "item-value": "deviceId",
                              variant: "outlined",
                              density: "comfortable",
                              "hide-details": "",
                              disabled: !hasCameraDevices.value || !cameraEnabled.value,
                              placeholder: "No camera detected",
                              class: "settings-panel__select",
                              "onUpdate:modelValue": handleCameraChange
                            }, {
                              "prepend-inner": _withCtx(() => [
                                _createVNode(_component_v_icon, {
                                  size: "24",
                                  color: "#222222"
                                }, {
                                  default: _withCtx(() => [...(_cache[25] || (_cache[25] = [
                                    _createTextVNode("mdi-video-plus-outline", -1)
                                  ]))]),
                                  _: 1
                                })
                              ]),
                              _: 1
                            }, 8, ["model-value", "items", "disabled"])
                          ])
                        ], 64))
                      : _createCommentVNode("", true)
                ])
              ])
            ])
          ], 32))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }))
}
}

})